/**
 * WishlistContext — a single source of truth for the currently signed-in
 * user's saved products.
 *
 * Responsibilities:
 *  • Load the user's wishlist from `/api/auth/wishlist` when they sign in.
 *  • Provide `has(productId)` to any card / detail view.
 *  • Provide `toggle(productId)` that:
 *      - redirects guests to /login so they don't tap on a dead heart
 *      - optimistically flips the state, calls `POST /api/auth/wishlist/{id}`,
 *        and rolls back on failure.
 *  • Reset itself when the user signs out.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WishlistContext = createContext(null);

export function WishlistProvider({ children }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [ids, setIds] = useState(() => new Set());
    const [loading, setLoading] = useState(false);

    // Hydrate on sign-in — with a request version so that if the user clicks
    // the heart while this GET is still in flight, the toggle's response
    // (which is newer) isn't overwritten by this stale hydration.
    const hydrationVersion = useMemo(() => ({ v: 0 }), []);
    useEffect(() => {
        if (!user) {
            setIds(new Set());
            return;
        }
        hydrationVersion.v += 1;
        const myVersion = hydrationVersion.v;
        setLoading(true);
        api.get("/auth/wishlist")
            .then((res) => {
                if (hydrationVersion.v === myVersion) {
                    setIds(new Set(res.data || []));
                }
            })
            .catch(() => { /* silent — heart just stays empty */ })
            .finally(() => { setLoading(false); });
    }, [user, hydrationVersion]);

    const has = useCallback((productId) => ids.has(productId), [ids]);

    const toggle = useCallback(async (productId, opts = {}) => {
        if (!user) {
            // Not signed in — remember where they were so we can bounce back.
            toast.info("Sign in to save this to your wishlist");
            navigate("/login?redirect=" + encodeURIComponent(window.location.pathname));
            return;
        }
        // Optimistic update (synchronous — heart flips immediately)
        const willAdd = !ids.has(productId);
        setIds((prev) => {
            const next = new Set(prev);
            if (willAdd) next.add(productId);
            else next.delete(productId);
            return next;
        });
        if (!opts.silent) toast.success(willAdd ? "Saved to wishlist" : "Removed from wishlist");
        // Invalidate any in-flight hydration so its stale response can't override us
        hydrationVersion.v += 1;
        try {
            const res = await api.post(`/auth/wishlist/${productId}`);
            // Reconcile with backend truth
            setIds(new Set(res.data.wishlist || []));
        } catch (e) {
            // Roll back optimistic update
            setIds((prev) => {
                const next = new Set(prev);
                if (willAdd) next.delete(productId);
                else next.add(productId);
                return next;
            });
            toast.error("Could not update wishlist");
        }
    }, [user, ids, navigate, hydrationVersion]);

    const value = useMemo(() => ({
        ids,
        count: ids.size,
        loading,
        has,
        toggle,
    }), [ids, loading, has, toggle]);

    return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}

export const useWishlist = () => {
    const ctx = useContext(WishlistContext);
    // Defensive default so components render even outside the provider
    // (e.g. during storybook / isolated tests).
    if (!ctx) return { ids: new Set(), count: 0, loading: false, has: () => false, toggle: () => {} };
    return ctx;
};
